"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { FaExclamationTriangle } from "react-icons/fa";
import { FaTrash } from "react-icons/fa6";
import { useAppDispatch, useAppSelector } from "@lib/hooks";
import { selectOption, clearOption } from "@lib/features/cart/cartSlice";
import {
    DOMESTIC_SHIPPING_CENTS,
    formatUsdCents,
    isDomesticShippingCountry,
} from "@lib/commerce";
import { countries } from "@/cart/Countries";
import {
    getWebsiteProductImageFitMode,
    getWebsiteProductImageUrl,
} from "@/lib/product-presentation";
import styles from "./page.module.css";

const CHECKOUT_SNAPSHOT_KEY = "uxd-checkout:last";

function isBlank(value) {
    return String(value || "").trim() === "";
}

function toPriceCents(value) {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return 0;
    }

    return Math.round(numericValue * 100);
}

function buildPricingState({
    subtotalCents,
    shippingCents,
    taxCents = null,
    isDomestic = false,
    status,
    message = "",
    taxRatePercent = null,
}) {
    return {
        subtotalCents,
        shippingCents,
        taxCents,
        totalCents: subtotalCents + shippingCents + (taxCents ?? 0),
        isDomestic,
        status,
        message,
        taxRatePercent,
    };
}

function writeCheckoutSnapshot(fields) {
    if (typeof window === "undefined") {
        return;
    }

    window.sessionStorage.setItem(
        CHECKOUT_SNAPSHOT_KEY,
        JSON.stringify({
            createdAt: Date.now(),
            fields,
        })
    );
}

export default function CartPage() {
    const dispatch = useAppDispatch();
    const items = useAppSelector((state) => state.cart.items);
    const router = useRouter();
    const lineItems = Object.entries(items);
    const cartItems = lineItems.filter(([, item]) => typeof item.option === "object" && item.option);

    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [country, setCountry] = useState("");
    const [fullName, setFullName] = useState("");
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [city, setCity] = useState("");
    const [zip, setZip] = useState("");
    const [stateField, setStateField] = useState("");
    const [notes, setNotes] = useState("");
    const [terms, setTerms] = useState(false);

    const [touched, setTouched] = useState({});
    const [missingFields, setMissingFields] = useState([]);
    const [checkoutError, setCheckoutError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pricing, setPricing] = useState(
        buildPricingState({
            subtotalCents: 0,
            shippingCents: 0,
            isDomestic: false,
            status: "empty",
        })
    );

    const markTouched = (field) =>
        setTouched((prev) => ({ ...prev, [field]: true }));

    useEffect(() => {
        const timer = setTimeout(() => {
            const autoTouched = {};
            if (!isBlank(email)) autoTouched.email = true;
            if (!isBlank(phone)) autoTouched.phone = true;
            if (!isBlank(country)) autoTouched.country = true;
            if (!isBlank(fullName)) autoTouched.fullName = true;
            if (!isBlank(address1)) autoTouched.address1 = true;
            if (!isBlank(city)) autoTouched.city = true;
            if (!isBlank(zip)) autoTouched.zip = true;
            if (!isBlank(stateField)) autoTouched.stateField = true;
            if (terms) autoTouched.terms = true;

            if (Object.keys(autoTouched).length) {
                setTouched((prev) => ({ ...prev, ...autoTouched }));
            }
        }, 200);

        return () => clearTimeout(timer);
    }, [email, phone, country, fullName, address1, city, zip, stateField, terms]);

    const isFormValid =
        !isBlank(email) &&
        !isBlank(phone) &&
        !isBlank(country) &&
        !isBlank(fullName) &&
        !isBlank(address1) &&
        !isBlank(city) &&
        !isBlank(zip) &&
        !isBlank(stateField) &&
        terms;

    const subtotalCents = cartItems.reduce(
        (sum, [, { option, quantity }]) => sum + toPriceCents(option?.price) * Math.max(1, Number(quantity || 1)),
        0
    );
    const subtotal = formatUsdCents(subtotalCents);
    const isDomestic = isDomesticShippingCountry(country);

    const productPayload = cartItems.map(([altId, { option, name, quantity }]) => ({
        alt_id: altId,
        options: [option.id],
        legacyBuyingOptionId: option.id,
        name: option.name,
        price: Number(option.price || 0),
        productName: name,
        quantity: Math.max(1, Number(quantity || 1)),
    }));
    const productPayloadJson = JSON.stringify(productPayload);

    useEffect(() => {
        if (!cartItems.length) {
            setPricing(
                buildPricingState({
                    subtotalCents: 0,
                    shippingCents: 0,
                    isDomestic: false,
                    status: "empty",
                })
            );
            return;
        }

        if (isBlank(country)) {
            setPricing(
                buildPricingState({
                    subtotalCents,
                    shippingCents: 0,
                    isDomestic: false,
                    status: "missing_country",
                    message: "Select a country to estimate shipping and tax.",
                })
            );
            return;
        }

        if (!isDomestic) {
            setPricing(
                buildPricingState({
                    subtotalCents,
                    shippingCents: 0,
                    taxCents: 0,
                    isDomestic: false,
                    status: "manual_quote",
                    message: "International shipping is quoted after checkout.",
                })
            );
            return;
        }

        const hasAddressForEstimate =
            !isBlank(address1) &&
            !isBlank(city) &&
            !isBlank(zip) &&
            !isBlank(stateField);

        if (!hasAddressForEstimate) {
            setPricing(
                buildPricingState({
                    subtotalCents,
                    shippingCents: DOMESTIC_SHIPPING_CENTS,
                    isDomestic: true,
                    status: "needs_address",
                    message: "Enter your address to estimate sales tax.",
                })
            );
            return;
        }

        const controller = new AbortController();
        const timer = setTimeout(async () => {
            const currentProductPayload = JSON.parse(productPayloadJson);

            setPricing(
                buildPricingState({
                    subtotalCents,
                    shippingCents: DOMESTIC_SHIPPING_CENTS,
                    isDomestic: true,
                    status: "loading",
                    message: "Updating tax estimate...",
                })
            );

            try {
                const response = await fetch("/api/cart-estimate", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    signal: controller.signal,
                    body: JSON.stringify({
                        products: currentProductPayload,
                        shipping: {
                            country,
                            address1,
                            address2,
                            city,
                            zip,
                            stateField,
                        },
                    }),
                });

                const payload = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(payload?.error || "Unable to estimate cart totals.");
                }

                setPricing(
                    buildPricingState({
                        subtotalCents: Number(payload?.subtotalCents || subtotalCents),
                        shippingCents: Number(payload?.shippingCents || DOMESTIC_SHIPPING_CENTS),
                        taxCents:
                            payload?.taxCents === null || payload?.taxCents === undefined
                                ? null
                                : Number(payload.taxCents),
                        isDomestic: Boolean(payload?.isDomestic),
                        status: String(payload?.status || "ready"),
                        message: String(payload?.message || ""),
                        taxRatePercent:
                            payload?.taxRatePercent === null || payload?.taxRatePercent === undefined
                                ? null
                                : Number(payload.taxRatePercent),
                    })
                );
            } catch (error) {
                if (controller.signal.aborted) {
                    return;
                }

                setPricing(
                    buildPricingState({
                        subtotalCents,
                        shippingCents: DOMESTIC_SHIPPING_CENTS,
                        isDomestic: true,
                        status: "tax_unavailable",
                        message:
                            error instanceof Error && error.message.trim()
                                ? error.message.trim()
                                : "Tax is calculated at checkout.",
                    })
                );
            }
        }, 350);

        return () => {
            controller.abort();
            clearTimeout(timer);
        };
    }, [
        productPayloadJson,
        subtotalCents,
        cartItems.length,
        country,
        address1,
        address2,
        city,
        zip,
        stateField,
        isDomestic,
    ]);

    const computeMissing = () => {
        const missing = [];
        if (isBlank(email)) missing.push("Email");
        if (isBlank(phone)) missing.push("Phone");
        if (isBlank(country)) missing.push("Country");
        if (isBlank(fullName)) missing.push("Name");
        if (isBlank(address1)) missing.push("Address Line 1");
        if (isBlank(city)) missing.push("City");
        if (isBlank(zip)) missing.push("ZIP");
        if (isBlank(stateField)) missing.push("State / Province");
        setMissingFields(missing);
    };

    useEffect(() => {
        if (terms) {
            const missing = [];
            if (isBlank(email)) missing.push("Email");
            if (isBlank(phone)) missing.push("Phone");
            if (isBlank(country)) missing.push("Country");
            if (isBlank(fullName)) missing.push("Name");
            if (isBlank(address1)) missing.push("Address Line 1");
            if (isBlank(city)) missing.push("City");
            if (isBlank(zip)) missing.push("ZIP");
            if (isBlank(stateField)) missing.push("State / Province");
            setMissingFields(missing);
        }
    }, [terms, email, phone, country, fullName, address1, city, zip, stateField]);

    const handleQtyChange = (altId, name, option, productImage) => (event) => {
        const value = parseInt(event.target.value, 10);
        const quantity = isNaN(value) || value < 1 ? 1 : value;
        dispatch(selectOption(altId, name, option, quantity, productImage));
    };

    const handleRemove = (altId) => {
        dispatch(clearOption(altId));
    };

    const handleCheckout = async () => {
        setTouched({
            email: true,
            phone: true,
            country: true,
            fullName: true,
            address1: true,
            city: true,
            zip: true,
            stateField: true,
            terms: true,
        });
        setCheckoutError("");
        computeMissing();

        if (!isFormValid || !cartItems.length) {
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch("/api/create-checkout-session", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    products: productPayload,
                    shipping: {
                        email,
                        phone,
                        country,
                        fullName,
                        address1,
                        address2,
                        city,
                        zip,
                        stateField,
                        notes,
                    },
                }),
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.url) {
                throw new Error(payload?.error || "Unable to create checkout session.");
            }

            writeCheckoutSnapshot({
                stripeID: "",
                shipping_warning: "",
                first_name: fullName.trim().split(" ")[0] || "",
                full_name: fullName,
                email,
                phone_number: phone,
                random_id: "",
                address1,
                address2,
                country,
                city,
                state: stateField,
                zip,
                notes,
                date_created: new Date().toLocaleDateString(),
                options: productPayload
                    .map((product) => {
                        const label = [product.productName, product.name].filter(Boolean).join(" - ");
                        return `${label} x ${product.quantity}`;
                    })
                    .join("<br>"),
                subtotal,
                tax: taxCents === null ? "" : formatUsdCents(taxCents),
                tax_rate: pricing.taxRatePercent === null || pricing.taxRatePercent === undefined
                    ? ""
                    : pricing.taxRatePercent,
                shipping: pricing.isDomestic ? formatUsdCents(shippingCents) : "Manual quote",
                final_price: formatUsdCents(displayTotalCents),
                portal_sync_status: "",
                order_details_unavailable: false,
            });

            router.push(payload.url);
        } catch (error) {
            setCheckoutError(
                error instanceof Error && error.message.trim()
                    ? error.message.trim()
                    : "Unable to continue to checkout."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        await handleCheckout();
    };

    const handleTermsChange = (event) => {
        const checked = event.target.checked;
        setTerms(checked);
        markTouched("terms");

        if (checked) {
            computeMissing();
        } else {
            setMissingFields([]);
        }
    };

    const uniqueCountries = countries.filter(
        (countryEntry, index, self) =>
            index === self.findIndex((candidate) => candidate.code === countryEntry.code)
    );

    const shippingCents = pricing.isDomestic ? pricing.shippingCents : 0;
    const taxCents = pricing.taxCents;
    const displayTotalCents =
        taxCents === null
            ? subtotalCents + shippingCents
            : subtotalCents + shippingCents + taxCents;

    let pricingMessage = pricing.message;
    if (pricing.status === "tax_unavailable" || pricing.status === "stripe_unavailable") {
        pricingMessage = "Tax will be finalized during checkout.";
    }
    if (pricing.status === "loading") {
        pricingMessage = "Updating tax estimate...";
    }

    return (
        <form className={`ux-container ${styles.cartWrapper}`} onSubmit={handleSubmit}>
            <div className={styles.cartItems}>
                <h3 className="mb-4">Cart Items</h3>
                {cartItems.length === 0 ? (
                    <p>Your cart is empty.</p>
                ) : (
                    <div className={styles.cartItemsList}>
                        <div className={styles.cartItemsHeader}>
                            <div className={styles.cartItemName}>Product</div>
                            <div className={styles.cartItemPrice}>Price</div>
                            <div className={styles.cartItemQty}>Qty</div>
                            <div className={styles.cartItemSubtotal}>Subtotal</div>
                            <div className={styles.cartItemRemove}></div>
                        </div>

                        {cartItems.map(([altId, { option, name, quantity, productImage }]) => {
                            const productForImage = { alt_id: altId };
                            const imageFitMode = getWebsiteProductImageFitMode(productForImage);
                            const productImageUrl = productImage || getWebsiteProductImageUrl(productForImage);
                            const imageClassName = [
                                styles.cartItemImage,
                                imageFitMode === "wide" ? styles.cartItemImageWide : "",
                                imageFitMode === "tall" ? styles.cartItemImageTall : "",
                            ].filter(Boolean).join(" ");

                            return (
                                <div key={altId} className={styles.cartItemRow}>
                                    <div className={styles.cartItemName}>
                                        <div className={styles.cartProductCell}>
                                            <div className={styles.cartItemImageFrame}>
                                                <Image
                                                    src={productImageUrl}
                                                    alt=""
                                                    fill
                                                    sizes="64px"
                                                    className={imageClassName}
                                                />
                                            </div>
                                            <div className={styles.cartProductText}>
                                                <span>{name}</span>
                                                <span>{option.name}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.cartItemPrice}>
                                        ${formatUsdCents(toPriceCents(option?.price))}
                                    </div>
                                    <div className={styles.cartItemQty}>
                                        <input
                                            type="number"
                                            min={1}
                                            value={quantity}
                                            className="form-control form-control-sm"
                                            aria-label={`${name} ${option.name} quantity`}
                                            onChange={handleQtyChange(altId, name, option, productImageUrl)}
                                        />
                                    </div>
                                    <div className={styles.cartItemSubtotal}>
                                        ${formatUsdCents(toPriceCents(option?.price) * Math.max(1, Number(quantity || 1)))}
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.cartItemRemove}
                                        aria-label={`Remove ${name} ${option.name} from cart`}
                                        onClick={() => handleRemove(altId)}
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            );
                        })}

                        <div className={styles.cartItemsFooter}>
                            <div>Subtotal</div>
                            <div>${subtotal}</div>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.shippingInformation}>
                <h3 className="mb-4">Shipping Information</h3>

                <div className="mb-3">
                    <label className="form-label" htmlFor="cart-email">Email address</label>
                    <input
                        id="cart-email"
                        type="email"
                        autoComplete="email"
                        className={`form-control ${touched.email && isBlank(email) ? "is-invalid" : ""}`}
                        placeholder="Enter email"
                        value={email}
                        aria-invalid={touched.email && isBlank(email)}
                        onChange={(event) => {
                            setEmail(event.target.value);
                            markTouched("email");
                        }}
                        onBlur={() => markTouched("email")}
                    />
                    {touched.email && isBlank(email) && <div className="invalid-feedback">Email is required.</div>}
                </div>

                <div className="mb-3">
                    <label className="form-label" htmlFor="cart-phone">Phone Number</label>
                    <input
                        id="cart-phone"
                        type="tel"
                        autoComplete="tel"
                        className={`form-control ${touched.phone && isBlank(phone) ? "is-invalid" : ""}`}
                        placeholder="+1 (xxx)-xxx-xxxx"
                        value={phone}
                        aria-invalid={touched.phone && isBlank(phone)}
                        onChange={(event) => {
                            setPhone(event.target.value);
                            markTouched("phone");
                        }}
                        onBlur={() => markTouched("phone")}
                    />
                    {touched.phone && isBlank(phone) && <div className="invalid-feedback">Phone is required.</div>}
                </div>

                <div className="mb-3">
                    <label className="form-label" htmlFor="cart-country">Country</label>
                    <select
                        id="cart-country"
                        className={`form-select ${touched.country && isBlank(country) ? "is-invalid" : ""}`}
                        value={country}
                        aria-invalid={touched.country && isBlank(country)}
                        onChange={(event) => {
                            setCountry(event.target.value);
                            markTouched("country");
                        }}
                        onBlur={() => markTouched("country")}
                    >
                        <option value="">Select Country</option>
                        {uniqueCountries.map(({ name, code }) => (
                            <option key={code} value={code}>
                                {name}
                            </option>
                        ))}
                    </select>
                    {touched.country && isBlank(country) && (
                        <div className="invalid-feedback">Country is required.</div>
                    )}
                </div>

                <div className="mb-3">
                    <label className="form-label" htmlFor="cart-full-name">Full Name</label>
                    <input
                        id="cart-full-name"
                        type="text"
                        autoComplete="name"
                        className={`form-control mb-2 ${touched.fullName && isBlank(fullName) ? "is-invalid" : ""}`}
                        placeholder="First and last name"
                        value={fullName}
                        aria-invalid={touched.fullName && isBlank(fullName)}
                        onChange={(event) => {
                            setFullName(event.target.value);
                            markTouched("fullName");
                        }}
                        onBlur={() => markTouched("fullName")}
                    />
                    {touched.fullName && isBlank(fullName) && (
                        <div className="invalid-feedback">Name is required.</div>
                    )}

                    <label className="form-label" htmlFor="cart-address-1">Address Line 1</label>
                    <input
                        id="cart-address-1"
                        type="text"
                        autoComplete="address-line1"
                        className={`form-control mb-2 ${touched.address1 && isBlank(address1) ? "is-invalid" : ""}`}
                        placeholder="Address Line 1"
                        value={address1}
                        aria-invalid={touched.address1 && isBlank(address1)}
                        onChange={(event) => {
                            setAddress1(event.target.value);
                            markTouched("address1");
                        }}
                        onBlur={() => markTouched("address1")}
                    />
                    {touched.address1 && isBlank(address1) && (
                        <div className="invalid-feedback">Address is required.</div>
                    )}

                    <label className="form-label" htmlFor="cart-address-2">Address Line 2</label>
                    <input
                        id="cart-address-2"
                        type="text"
                        autoComplete="address-line2"
                        className="form-control mb-2"
                        placeholder="Address Line 2 (optional)"
                        value={address2}
                        onChange={(event) => setAddress2(event.target.value)}
                    />
                </div>

                <div className={styles.locationRow}>
                    <div className="flex-fill">
                        <label className="form-label" htmlFor="cart-city">City</label>
                        <input
                            id="cart-city"
                            type="text"
                            autoComplete="address-level2"
                            className={`form-control ${touched.city && isBlank(city) ? "is-invalid" : ""}`}
                            placeholder="City"
                            value={city}
                            aria-invalid={touched.city && isBlank(city)}
                            onChange={(event) => {
                                setCity(event.target.value);
                                markTouched("city");
                            }}
                            onBlur={() => markTouched("city")}
                        />
                    </div>
                    <div className="flex-fill">
                        <label className="form-label" htmlFor="cart-zip">ZIP</label>
                        <input
                            id="cart-zip"
                            type="text"
                            autoComplete="postal-code"
                            className={`form-control ${touched.zip && isBlank(zip) ? "is-invalid" : ""}`}
                            placeholder="ZIP"
                            value={zip}
                            aria-invalid={touched.zip && isBlank(zip)}
                            onChange={(event) => {
                                setZip(event.target.value);
                                markTouched("zip");
                            }}
                            onBlur={() => markTouched("zip")}
                        />
                    </div>
                </div>
                {touched.city && isBlank(city) && (
                    <div className="invalid-feedback d-block">City is required.</div>
                )}
                {touched.zip && isBlank(zip) && (
                    <div className="invalid-feedback d-block">ZIP is required.</div>
                )}

                <label className="form-label" htmlFor="cart-state">State / Province</label>
                <input
                    id="cart-state"
                    type="text"
                    autoComplete="address-level1"
                    className={`form-control mb-3 ${touched.stateField && isBlank(stateField) ? "is-invalid" : ""}`}
                    placeholder="State"
                    value={stateField}
                    aria-invalid={touched.stateField && isBlank(stateField)}
                    onChange={(event) => {
                        setStateField(event.target.value);
                        markTouched("stateField");
                    }}
                    onBlur={() => markTouched("stateField")}
                />
                {touched.stateField && isBlank(stateField) && (
                    <div className="invalid-feedback">State is required.</div>
                )}

                <label className="form-label" htmlFor="cart-notes">Notes / Comments</label>
                <textarea
                    id="cart-notes"
                    className="form-control mb-3"
                    placeholder="Notes or comments (optional)"
                    rows={3}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                />

                <div className="form-check mb-3">
                    <input
                        type="checkbox"
                        className={`form-check-input ${touched.terms && !terms ? "is-invalid" : ""}`}
                        id="terms"
                        checked={terms}
                        onChange={handleTermsChange}
                        onBlur={() => markTouched("terms")}
                    />
                    <label className="form-check-label" htmlFor="terms">
                        I agree to Terms & Conditions
                    </label>
                    {touched.terms && !terms && (
                        <div className="invalid-feedback d-block">You must accept terms.</div>
                    )}
                </div>
                {terms && missingFields.length > 0 && (
                    <div className="alert alert-warning">
                        Please fill the following fields: {missingFields.join(", ")}
                    </div>
                )}
            </div>

            <div className={styles.orderSummary}>
                <h3 className="mb-4">Order Summary</h3>

                <ul className={`list-unstyled mb-3 ${styles.summaryList}`}>
                    <li className={styles.summaryRow}>
                        <span>Subtotal</span>
                        <strong>${subtotal}</strong>
                    </li>

                    <li className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>
                            Shipping{" "}
                            {pricing.isDomestic ? <span>FedEx Ground</span> : null}
                        </span>
                        <span>
                            {pricing.isDomestic ? (
                                `$${formatUsdCents(shippingCents)}`
                            ) : pricing.status === "manual_quote" ? (
                                "Manual quote"
                            ) : (
                                "$0.00"
                            )}
                        </span>
                    </li>

                    <li className={styles.summaryRow}>
                        <span>
                            Sales Tax
                            {pricing.taxRatePercent !== null && pricing.taxRatePercent > 0
                                ? ` (${pricing.taxRatePercent}%)`
                                : ""}
                        </span>
                        <span>
                            {taxCents === null ? (
                                pricing.isDomestic ? "Estimated after address" : "$0.00"
                            ) : (
                                `$${formatUsdCents(taxCents)}`
                            )}
                        </span>
                    </li>
                </ul>

                {pricingMessage && (
                    <div className={styles.summaryHint}>
                        {pricing.status === "manual_quote" && <FaExclamationTriangle size={16} />}
                        <span>{pricingMessage}</span>
                    </div>
                )}

                <hr />
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                        <strong>Estimated Total</strong>
                        {taxCents === null && pricing.isDomestic && (
                            <div className={styles.summarySubtext}>Final tax is confirmed during checkout.</div>
                        )}
                    </div>
                    <strong>${formatUsdCents(displayTotalCents)}</strong>
                </div>

                <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={!isFormValid || cartItems.length === 0 || isSubmitting}
                >
                    {isSubmitting
                        ? "Preparing checkout..."
                        : isFormValid && cartItems.length > 0
                            ? "Proceed to Checkout"
                            : "Please complete the form and accept terms"}
                </button>

                {checkoutError && (
                    <div className="alert alert-danger mt-3 mb-0">
                        {checkoutError}
                    </div>
                )}

                {!isDomestic && !isBlank(country) && (
                    <div className={styles.outOfCountryNotification}>
                        <FaExclamationTriangle size={28} className="text-warning flex-shrink-0" />
                        <div>
                            Your address is outside our country. We will email you the exact shipping cost within one business day of placing your order.
                        </div>
                    </div>
                )}
            </div>
        </form>
    );
}
